import { useMemo, useState } from "react";
import {
  X, Plus, Pencil, Trash2, Loader2, Check, Users, HardHat, Info, Search,
} from "lucide-react";
import { useInternalUsers } from "@/features/tasks/hooks/useInternalUsers";
import { UserAvatar } from "@/components/ui/UserAvatar";
import {
  useAccessGroups,
  useCreateAccessGroup,
  useUpdateAccessGroup,
  useDeleteAccessGroup,
  useContractorsList,
} from "../hooks/useRepository";
import type { AccessGroup } from "../types";

interface Props {
  onClose: () => void;
}

const INFO_TEXT =
  "Grupa dostępu to nazwany zestaw użytkowników i/lub podwykonawców. " +
  "Po przypisaniu grupy do zasobu (w zakładce „Dostęp” danego zasobu, pole „Grupy dostępu”) " +
  "wszyscy jej członkowie uzyskują dostęp do tego zasobu — niezależnie od jego typu " +
  "(dokument, link, dokument podwykonawcy). Dodanie podwykonawcy do grupy nadaje dostęp " +
  "wszystkim użytkownikom tej firmy podwykonawczej. Grupy są prywatne — widzisz i zarządzasz " +
  "wyłącznie własnymi grupami.";

export function AccessGroupsModal({ onClose }: Props) {
  const { data: groups = [], isLoading } = useAccessGroups();
  const { mutateAsync: deleteGroup } = useDeleteAccessGroup();

  // null = list view; "new" = create form; AccessGroup = edit form
  const [editing, setEditing] = useState<AccessGroup | "new" | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  async function handleDelete(group: AccessGroup) {
    if (!confirm(`Usunąć grupę „${group.name}”? Zostanie też odłączona od zasobów.`)) return;
    setDeletingId(group.id);
    try {
      await deleteGroup(group.id);
    } finally {
      setDeletingId(null);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center sm:p-4" onClick={onClose}>
      <div
        className="bg-white dark:bg-gray-900 shadow-xl w-full sm:max-w-lg flex flex-col rounded-t-2xl sm:rounded-xl max-h-[92dvh] sm:max-h-[90vh]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center gap-2 px-5 py-4 border-b border-gray-100 dark:border-gray-700">
          <Users size={17} className="text-[var(--color-primary)]" />
          <h2 className="text-base font-semibold text-gray-900 dark:text-white">
            Grupy dostępu
          </h2>
          <span className="group relative inline-flex">
            <Info size={15} className="text-gray-400 hover:text-[var(--color-primary)] cursor-help" />
            <span className="pointer-events-none absolute left-1/2 top-6 z-10 hidden group-hover:block w-72 -translate-x-1/2 rounded-lg bg-gray-900 dark:bg-gray-700 px-3 py-2 text-xs leading-relaxed text-white shadow-lg">
              {INFO_TEXT}
            </span>
          </span>
          <button onClick={onClose} className="ml-auto text-gray-400 hover:text-gray-700 dark:hover:text-gray-200">
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5">
          {editing ? (
            <AccessGroupForm
              group={editing === "new" ? null : editing}
              onDone={() => setEditing(null)}
              onCancel={() => setEditing(null)}
            />
          ) : isLoading ? (
            <div className="flex justify-center py-10">
              <Loader2 size={24} className="animate-spin text-gray-400" />
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              {groups.length === 0 && (
                <p className="text-sm text-gray-400 text-center py-6">
                  Nie masz jeszcze żadnych grup dostępu.
                </p>
              )}

              {groups.map((g) => (
                <div
                  key={g.id}
                  className="flex items-center gap-3 p-3 border border-gray-200 dark:border-gray-700 rounded-xl"
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                      {g.name}
                    </p>
                    <p className="text-xs text-gray-400 mt-0.5 flex items-center gap-3">
                      <span className="inline-flex items-center gap-1">
                        <Users size={11} /> {g.members.length}
                      </span>
                      <span className="inline-flex items-center gap-1">
                        <HardHat size={11} /> {g.contractors.length}
                      </span>
                    </p>
                  </div>
                  <button
                    onClick={() => setEditing(g)}
                    title="Edytuj"
                    className="p-1.5 rounded-lg text-gray-400 hover:text-[var(--color-primary)] hover:bg-[var(--color-primary-dim)] dark:hover:bg-blue-900/20"
                  >
                    <Pencil size={15} />
                  </button>
                  <button
                    onClick={() => handleDelete(g)}
                    disabled={deletingId === g.id}
                    title="Usuń"
                    className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                  >
                    {deletingId === g.id ? <Loader2 size={15} className="animate-spin" /> : <Trash2 size={15} />}
                  </button>
                </div>
              ))}

              <button
                onClick={() => setEditing("new")}
                className="mt-1 flex items-center justify-center gap-2 w-full px-3 py-2 text-sm text-gray-500 dark:text-gray-400 hover:text-[var(--color-primary)] dark:hover:text-blue-400 rounded-xl border border-dashed border-gray-300 dark:border-gray-700 hover:border-[var(--color-primary)]/50 transition-colors"
              >
                <Plus size={15} />
                Nowa grupa
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

interface FormProps {
  group: AccessGroup | null; // null = create
  onDone: () => void;
  onCancel: () => void;
}

function AccessGroupForm({ group, onDone, onCancel }: FormProps) {
  const { data: allUsers = [] } = useInternalUsers();
  const { data: contractors = [] } = useContractorsList();
  const { mutateAsync: createGroup, isPending: creating } = useCreateAccessGroup();
  const { mutateAsync: updateGroup, isPending: updating } = useUpdateAccessGroup();

  const [name, setName] = useState(group?.name ?? "");
  const [memberIds, setMemberIds] = useState<string[]>(group?.members.map((m) => m.id) ?? []);
  const [contractorIds, setContractorIds] = useState<string[]>(group?.contractors.map((c) => c.id) ?? []);
  const [userSearch, setUserSearch] = useState("");
  const [contractorSearch, setContractorSearch] = useState("");

  const isPending = creating || updating;

  const internalUsers = useMemo(
    () => allUsers.filter((u) => u.role !== "contractor"),
    [allUsers],
  );
  const filteredUsers = useMemo(() => {
    const q = userSearch.trim().toLowerCase();
    return q ? internalUsers.filter((u) => u.full_name.toLowerCase().includes(q)) : internalUsers;
  }, [internalUsers, userSearch]);
  const filteredContractors = useMemo(() => {
    const q = contractorSearch.trim().toLowerCase();
    return q ? contractors.filter((c) => c.name.toLowerCase().includes(q)) : contractors;
  }, [contractors, contractorSearch]);

  function toggleMember(id: string) {
    setMemberIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }
  function toggleContractor(id: string) {
    setContractorIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }

  async function handleSave() {
    if (!name.trim()) return;
    const payload = { name: name.trim(), member_ids: memberIds, contractor_ids: contractorIds };
    if (group) {
      await updateGroup({ id: group.id, data: payload });
    } else {
      await createGroup(payload);
    }
    onDone();
  }

  return (
    <div className="flex flex-col gap-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
          Nazwa grupy <span className="text-red-500">*</span>
        </label>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          autoFocus
          placeholder="np. Zespół projektu X"
          className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)] bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
        />
      </div>

      {/* Members */}
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider flex items-center gap-1.5">
            <Users size={12} /> Użytkownicy
            {memberIds.length > 0 && <span className="text-[var(--color-primary)]">({memberIds.length})</span>}
          </p>
        </div>
        <div className="relative mb-1.5">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            value={userSearch}
            onChange={(e) => setUserSearch(e.target.value)}
            placeholder="Szukaj użytkownika…"
            className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)] bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
          />
        </div>
        <div className="max-h-40 overflow-y-auto flex flex-col gap-1 p-1 border border-gray-100 dark:border-gray-800 rounded-lg">
          {filteredUsers.map((u) => (
            <label key={u.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 rounded px-1.5 py-1">
              <input
                type="checkbox"
                checked={memberIds.includes(u.id)}
                onChange={() => toggleMember(u.id)}
                className="accent-[var(--color-primary)]"
              />
              <UserAvatar fullName={u.full_name} profilePictureUrl={u.profile_picture_url} size="xs" />
              <span className="text-gray-700 dark:text-gray-300 truncate">{u.full_name}</span>
              <span className="text-xs text-gray-400 ml-auto">{u.role}</span>
            </label>
          ))}
          {filteredUsers.length === 0 && (
            <p className="text-xs text-gray-400 text-center py-3">Brak użytkowników.</p>
          )}
        </div>
      </div>

      {/* Contractors */}
      <div>
        <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider flex items-center gap-1.5 mb-1.5">
          <HardHat size={12} /> Podwykonawcy
          {contractorIds.length > 0 && <span className="text-[var(--color-primary)]">({contractorIds.length})</span>}
        </p>
        <div className="relative mb-1.5">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            value={contractorSearch}
            onChange={(e) => setContractorSearch(e.target.value)}
            placeholder="Szukaj podwykonawcy…"
            className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)] bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
          />
        </div>
        <div className="max-h-40 overflow-y-auto flex flex-col gap-1 p-1 border border-gray-100 dark:border-gray-800 rounded-lg">
          {filteredContractors.map((c) => (
            <label key={c.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 rounded px-1.5 py-1">
              <input
                type="checkbox"
                checked={contractorIds.includes(c.id)}
                onChange={() => toggleContractor(c.id)}
                className="accent-[var(--color-primary)]"
              />
              <span className="text-gray-700 dark:text-gray-300 truncate">{c.name}</span>
              {c.short_name && <span className="text-xs text-gray-400 ml-auto">{c.short_name}</span>}
            </label>
          ))}
          {filteredContractors.length === 0 && (
            <p className="text-xs text-gray-400 text-center py-3">Brak podwykonawców.</p>
          )}
        </div>
      </div>

      <div className="flex justify-end gap-2 pt-1">
        <button
          onClick={onCancel}
          className="px-3 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg"
        >
          Anuluj
        </button>
        <button
          onClick={handleSave}
          disabled={isPending || !name.trim()}
          className="flex items-center gap-1.5 px-4 py-1.5 bg-[var(--color-primary)] text-white text-sm rounded-lg hover:bg-[var(--color-primary-hover)] disabled:opacity-50"
        >
          {isPending ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />}
          {group ? "Zapisz" : "Utwórz"}
        </button>
      </div>
    </div>
  );
}
